/**
 * EXECUTOR-GAP-PANEL (s199) — the ONE visibility declaration for the "What your executor still
 * needs" panel.
 *
 * ## Why this file exists at all
 *
 * Reade's requirement, ruled s199: **hiding this later must be ONE change, not a hunt.** The
 * s171 `PLANNER_ONLY_CATEGORIES` lesson (F-S170.1) is that a visibility condition scattered across
 * several surfaces drifts — one gets the filter, one gets the gate, and the third gets neither,
 * which is a hole rather than a half-measure. So the decision lives here, in one predicate, and
 * every consumer asks it.
 *
 * ## What changed at D-199.14, and why the shape survived it
 *
 * D-199.11 put the panel on its own route and left a summary card on the dashboard linking to it.
 * That shipped and was verified working — and was then found to list all 17 records with their gap
 * rows and NO inputs, above a link to a page listing the same records again, with the inputs. The
 * planner read the identical list twice and only the second copy worked. **D-199.14 deleted the
 * page**: the panel is mounted on the dashboard, and that is where you type.
 *
 * The requirement did not change, only its target — the predicate now gates the dashboard MOUNT
 * instead of a route and a nav entry. `EXECUTOR_NEEDS_PATH` is gone with the route it named: a path
 * constant for a route that no longer exists is precisely the dead reference this file exists to
 * prevent.
 *
 * ## What membership means
 *
 * The panel is PLANNER-ONLY. It edits the planner's own `generic_entries`, which the read-only
 * shells have no write path for at all — the server refuses an executor/administrator write with
 * 403 regardless. This declaration is the surface half of that, not the security boundary.
 */

/** The panel's heading. One string, so the card title and any future surface agree. */
export const EXECUTOR_NEEDS_TITLE = "What your executor still needs";

/**
 * 🔴 THE ONE SWITCH. Flip to `false` to hide the panel everywhere — today that is the dashboard
 * mount, and any future surface reads the same predicate. Do not add a second condition beside a
 * consumer; change this.
 */
export const EXECUTOR_NEEDS_PANEL_ENABLED = true;

/**
 * The per-surface predicate. Every consumer asks THIS — never a bare role comparison and never a
 * bare read of the flag.
 *
 * `role` is the authenticated user's role (`users.role`). Only the planner ("client") sees the
 * panel: an executor's reading of the plan is the read-only category surface, and an administrator
 * has no reason to be inside one planner's gap list at all. The dashboard's own `!readOnly` gate
 * already excludes an advisor viewing a client, so this is belt-and-braces there rather than the
 * only guard — which is the correct relationship, not a redundancy to tidy away.
 */
export function isExecutorNeedsPanelVisible(role: string | null | undefined): boolean {
  return EXECUTOR_NEEDS_PANEL_ENABLED && role === "client";
}
